import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useLocation, BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { gapi } from 'gapi-script';
import './index.css';
import ScrollToTop from './components/ScrollToTop';
import { SocketProvider } from './context/SocketContext';
import { ArtistsProvider } from './context/ArtistsContext';

// Eagerly loaded — on the critical path for every visitor
import HomePage from './components/HomePage/HomePage';
import Newlogin from './components/Login/newlogin';

// Lazy loaded — only fetched when the user navigates to that route
const CardList                = lazy(() => import('./components/CardList'));
const AboutPage               = lazy(() => import('./components/AboutPage'));
const EventPage               = lazy(() => import('./components/EventPage'));
const LoggedInHomePage        = lazy(() => import('./components/HomePage/LoggedInHomePage'));
const LoggedInHomePageArtist  = lazy(() => import('./components/HomePage/LoggedInHomePageArtist'));
const ArtistProfilePage       = lazy(() => import('./components/HomePage/ArtistProfilePage'));
const AllArtistsPage          = lazy(() => import('./components/HomePage/AllArtistsPage'));
const AllBandsPage            = lazy(() => import('./components/HomePage/AllBandsPage'));
const JammingPage             = lazy(() => import('./components/Jamming'));
const HowItWorksPage          = lazy(() => import('./components/HowItWorksPage'));
const ProfilePage             = lazy(() => import('./components/HomePage/ProfilePage'));
const PublicArtistPage        = lazy(() => import('./components/HomePage/PublicArtistPage'));
const PublicArtistProfilePage = lazy(() => import('./components/HomePage/PublicArtistProfilePage'));
const PublicBandProfilePage   = lazy(() => import('./components/HomePage/PublicBandProfilePage'));
const CreateBandPage          = lazy(() => import('./components/HomePage/CreateBandPage'));
const EditBandPage            = lazy(() => import('./components/HomePage/EditBandPage'));
const FAQPage                 = lazy(() => import('./components/HomePage/FAQPage'));
const VerifyEmail             = lazy(() => import('./components/Login/VerifyEmail'));
const EventDashboard          = lazy(() => import('./components/EventDashboard'));
const JammingHostDashboard    = lazy(() => import('./components/JammingHostDashboard'));
const UserRequests            = lazy(() => import('./components/UserRequests'));
const PrivacyPolicy           = lazy(() => import('./components/HomePage/PrivacyPolicy'));
const TermsOfService          = lazy(() => import('./components/HomePage/TermsOfService'));
const CookiePolicy            = lazy(() => import('./components/HomePage/CookiePolicy'));
const RefundPolicy            = lazy(() => import('./components/HomePage/RefundPolicy'));
const PaymentSuccess          = lazy(() => import('./components/PaymentSuccess'));
const LoggedInHomePageVenue   = lazy(() => import('./components/HomePage/LoggedInHomePageVenue'));
const VenueProfilePage        = lazy(() => import('./components/HomePage/VenueProfilePage'));
const AllVenuesPage           = lazy(() => import('./components/HomePage/AllVenuesPage'));
const PublicVenueProfilePage  = lazy(() => import('./components/HomePage/PublicVenueProfilePage'));
const VocalAnalysisPage       = lazy(() => import('./components/VocalAnalysis/VocalAnalysisPage'));
const NewloginWrapped = Newlogin;

function ProtectedRedirect({ children, role, allowHomeRedirect }) {
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  const storedRole = (localStorage.getItem('role') || '').toLowerCase();
  const location = window.location.pathname;

  if (isLoggedIn) {
    if (allowHomeRedirect && (location === '/' || location === '/home')) {
      if (storedRole === 'artist') {
        return <Navigate to="/loggedInHomePageArtist" replace />;
      } else if (storedRole === 'user') {
        return <Navigate to="/loggedInHome" replace />;
      } else if (storedRole === 'venue' || storedRole === 'host') {
        return <Navigate to="/loggedInHomeVenue" replace />;
      }
    }
    if (storedRole === 'artist' && location !== '/loggedInHomePageArtist') {
      return <Navigate to="/loggedInHomePageArtist" replace />;
    } else if (storedRole === 'user' && location !== '/loggedInHome') {
      return <Navigate to="/loggedInHome" replace />;
    } else if ((storedRole === 'venue' || storedRole === 'host') && location !== '/loggedInHomeVenue') {
      return <Navigate to="/loggedInHomeVenue" replace />;
    }
  }
  return children;
}

function AppContent({ isLoggedIn, handleLogout, handleLogin }) {
  const location = useLocation();

  return (
    <>
    <ScrollToTop />
    <Suspense fallback={
      <div style={{ background: '#0F0F23', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 48, height: 48, border: '3px solid #4338CA', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    }>
      <Routes>
        <Route path="/" element={
          <ProtectedRedirect allowHomeRedirect>
            <HomePage />
          </ProtectedRedirect>
        } />
        <Route path="/home" element={
          <ProtectedRedirect allowHomeRedirect>
            <HomePage />
          </ProtectedRedirect>
        } />
        <Route path="/jamming" element={<JammingPage />} />
        <Route path="/loggedInHome" element={
          <ProtectedRedirect role="user">
            <LoggedInHomePage />
          </ProtectedRedirect>
        } />
        <Route path="/loggedInHomePageArtist" element={
          <ProtectedRedirect role="artist">
            <LoggedInHomePageArtist />
          </ProtectedRedirect>
        } />
        <Route path="/signup" element={
          <ProtectedRedirect>
            <Newlogin />
          </ProtectedRedirect>
        } />
        <Route path="/login" element={
          <ProtectedRedirect>
            <Newlogin onLogin={handleLogin} />
          </ProtectedRedirect>
        } />
        <Route path="/" element={<CardList />} />
        <Route path="/cards" element={<CardList />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/event/:eventId" element={<EventPage />} />
        <Route path="/artist-profile/:_id" element={<ArtistProfilePage />} />
        <Route path="/ArtistProfilePage/:_id" element={<ArtistProfilePage />} />
        <Route path="/all-artists" element={<AllArtistsPage />} />
        <Route path="/all-bands" element={<AllBandsPage />} />
        <Route path="/create-band" element={<CreateBandPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/ProfilePage/:userId?" element={<ProfilePage />} />
        <Route path="/public-artist/:_id" element={<PublicArtistPage />} />
        <Route path="/publicartistprofilepage/:_id" element={<PublicArtistProfilePage />} />
        <Route path="/public-band/:_id" element={<PublicBandProfilePage />} />
        <Route path="/edit-band/:id" element={<EditBandPage />} />
        <Route path="/eventdashboard" element={<EventDashboard />} />
        <Route path="/jamming-dashboard" element={<JammingHostDashboard />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/userrequests" element={<UserRequests />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/reset-password" element={<NewloginWrapped />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/refund" element={<RefundPolicy />} />
        <Route path="/payment-success/:merchantTransactionId" element={<PaymentSuccess />} />
        <Route path="/loggedInHomeVenue" element={<LoggedInHomePageVenue />} />
        <Route path="/venue-profile" element={<VenueProfilePage />} />
        <Route path="/all-venues" element={<AllVenuesPage />} />
        <Route path="/public-venue/:_id" element={<PublicVenueProfilePage />} />
        <Route path="/vocal-analysis" element={<VocalAnalysisPage />} />
      </Routes>
    </Suspense>
    </>
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [logoutTimerId, setLogoutTimerId] = useState(null);

  const getStoredExpiry = () => {
    const exp = localStorage.getItem('token_exp');
    return exp ? Number(exp) : null;
  };

  const parseJwtExpiry = (token) => {
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;
      // Properly handle base64url padding
      let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (base64.length % 4) base64 += '=';
      const payload = JSON.parse(atob(base64));
      if (payload && payload.exp) return payload.exp * 1000; // ms
      return null;
    } catch (_) {
      return null;
    }
  };

  const scheduleAutoLogout = (expiryMs) => {
    if (logoutTimerId) clearTimeout(logoutTimerId);
    if (!expiryMs) return;
    const now = Date.now();
    const remaining = expiryMs - now;
    if (remaining <= 0) {
      handleLogout(true); // expired already
      return;
    }
    const id = setTimeout(() => handleLogout(true), remaining);
    setLogoutTimerId(id);
  };

  const handleLogin = () => {
    setIsLoggedIn(true);
    localStorage.setItem('isLoggedIn', 'true');
    const token = localStorage.getItem('token');
    const expMs = parseJwtExpiry(token);
    const fallbackExpiryMs = Date.now() + 60 * 60 * 1000; // 1 hour fallback
    if (expMs) {
      localStorage.setItem('token_exp', String(expMs));
      scheduleAutoLogout(expMs);
    } else if (token) {
      localStorage.setItem('token_exp', String(fallbackExpiryMs));
      scheduleAutoLogout(fallbackExpiryMs);
    }
  };

  const handleLogout = (expired = false) => {
    setIsLoggedIn(false);
    // Clear auth/session data (both localStorage and sessionStorage) including role & token
    const keysToClear = ['isLoggedIn', 'userId', 'role', 'token', 'token_exp'];
    keysToClear.forEach(k => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
    if (logoutTimerId) clearTimeout(logoutTimerId);
    setLogoutTimerId(null);
    if (expired) {
      // Redirect to login (forced logout / token expiry)
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    }
  };

  const initializeGapi = () => {
    gapi.client.init({
      clientId: '975199237692-i5hc087qe2nb69dlu7f310bjp4m2hirl.apps.googleusercontent.com',
      scope: '',
    });
  };

  useEffect(() => {
    gapi.load('client:auth2', initializeGapi);
    const loggedInStatus = localStorage.getItem('isLoggedIn');
    if (loggedInStatus === 'true') {
      setIsLoggedIn(true);
      const expMs = getStoredExpiry();
      if (expMs && Date.now() >= expMs) {
        handleLogout(true);
      } else {
        scheduleAutoLogout(expMs);
      }
    }
  }, []);

  // Intercept fetch responses for 401/403 to force logout (forceLogout flag from backend middleware)
  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (response.status === 401 || response.status === 403) {
        let forceLogoutFlag = false;
        try {
          const cloned = response.clone();
          const ct = (cloned.headers.get('content-type') || '').toLowerCase();
          if (ct.includes('application/json')) {
            const data = await cloned.json();
            if (data && data.forceLogout === true) {
              forceLogoutFlag = true;
            }
          }
        } catch (_) {
          // ignore parse errors
        }
        const expMs = getStoredExpiry();
        // Only logout automatically if backend explicitly requests or token actually expired
        if (forceLogoutFlag || (expMs && Date.now() >= expMs)) {
          handleLogout(true);
        } else {
          // If token expiry missing, do NOT force logout; log diagnostic for debugging
          if (!expMs) {
            console.warn('[Auth] Missing token_exp on 401/403; ignoring auto logout.');
          }
        }
      }
      return response;
    };
    return () => { window.fetch = originalFetch; };
  }, []);

  // Cross-tab sync (logout on other tabs)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'token_exp') {
        const expMs = getStoredExpiry();
        scheduleAutoLogout(expMs);
      }
      if (e.key === 'token' && e.newValue === null) {
        handleLogout(true);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <SocketProvider>
      <Router>
        <ArtistsProvider>
          <AppContent
            isLoggedIn={isLoggedIn}
            handleLogout={handleLogout}
            handleLogin={handleLogin}
          />
        </ArtistsProvider>
      </Router>
    </SocketProvider>
  );
}

export default App;